"use client";

import { IconHeading } from "@/components/platform/v2/IconHeading";
import { useState } from "react";
import { z } from "zod";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/platform/v2/ui/button";
import { Input } from "@/components/platform/v2/ui/input";
import { Label } from "@/components/platform/v2/ui/label";
import { Textarea } from "@/components/platform/v2/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export type ProgramType = "partner" | "ambassador" | "unsure";

const applicationSchema = z.object({
  name: z.string().trim().min(2, "Укажите имя").max(100),
  company: z.string().trim().min(2, "Укажите компанию или проект").max(160),
  website: z
    .string()
    .trim()
    .min(3, "Укажите сайт или социальную сеть")
    .max(300),
  business: z
    .string()
    .trim()
    .min(10, "Коротко расскажите, чем занимается бизнес")
    .max(700),
  audience: z
    .string()
    .trim()
    .min(3, "Опишите размер или характеристику аудитории")
    .max(500),
  promotion: z
    .string()
    .trim()
    .min(10, "Расскажите, как планируете рекомендовать VCENTRE.IT")
    .max(1000),
  contact: z
    .string()
    .trim()
    .min(5, "Укажите телефон, email или Telegram")
    .max(255),
  comment: z.string().trim().max(1000).optional().or(z.literal("")),
});

const emptyForm = {
  name: "",
  company: "",
  website: "",
  business: "",
  audience: "",
  promotion: "",
  contact: "",
  comment: "",
};

interface Props {
  program: ProgramType;
  onProgramChange: (program: ProgramType) => void;
}

const programLabels: Record<ProgramType, string> = {
  partner: "Партнёр",
  ambassador: "Амбассадор",
  unsure: "Не знаю — предложите формат",
};

const PartnerApplicationForm = ({ program, onProgramChange }: Props) => {
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState<"idle" | "sending" | "success">("idle");
  const { toast } = useToast();

  const update =
    (field: keyof typeof emptyForm) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((current) => ({ ...current, [field]: event.target.value }));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const result = applicationSchema.safeParse(form);
    if (!result.success) {
      toast({
        title: "Проверьте данные",
        description:
          result.error.issues[0]?.message ?? "Заполните обязательные поля",
        variant: "destructive",
      });
      return;
    }

    setStatus("sending");
    try {
      const stored = JSON.parse(
        localStorage.getItem("partner-applications") || "[]",
      );
      const applications = Array.isArray(stored) ? stored : [];
      applications.push({
        ...result.data,
        program: programLabels[program],
        source: "Страница «Партнёрам»",
        status: "новая заявка",
        createdAt: new Date().toISOString(),
      });
      localStorage.setItem(
        "partner-applications",
        JSON.stringify(applications),
      );
      setStatus("success");
    } catch {
      setStatus("idle");
      toast({
        title: "Не удалось отправить заявку",
        description: "Попробуйте ещё раз.",
        variant: "destructive",
      });
    }
  };

  if (status === "success") {
    return (
      <div className="surface-quiet flex min-h-[300px] flex-col items-center justify-center text-center">
        <IconHeading
          as="h3"
          icon={
            <CheckCircle2
              className="mb-5 h-12 w-12 text-primary"
              aria-hidden="true"
            />
          }
          className="display-3 mb-3"
        >
          Спасибо.
        </IconHeading>
        <p className="body-text max-w-xl">
          Мы рассмотрим проект и свяжемся с вами, если видим потенциал для
          сотрудничества.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-7" noValidate>
      <fieldset>
        <legend className="mb-3 text-sm font-bold text-foreground">
          Какой формат интересует?
        </legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {(Object.keys(programLabels) as ProgramType[]).map((id) => (
            <Button
              key={id}
              type="button"
              variant={program === id ? "site" : "siteOutline"}
              className="w-full"
              aria-pressed={program === id}
              onClick={() => onProgramChange(id)}
            >
              {programLabels[id]}
            </Button>
          ))}
        </div>
      </fieldset>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Имя" id="partner-name">
          <Input
            id="partner-name"
            value={form.name}
            onChange={update("name")}
            maxLength={100}
            required
          />
        </Field>
        <Field label="Компания / проект" id="partner-company">
          <Input
            id="partner-company"
            value={form.company}
            onChange={update("company")}
            maxLength={160}
            required
          />
        </Field>
        <Field label="Сайт или социальная сеть" id="partner-website">
          <Input
            id="partner-website"
            value={form.website}
            onChange={update("website")}
            maxLength={300}
            required
          />
        </Field>
        <Field label="Контакт" id="partner-contact">
          <Input
            id="partner-contact"
            value={form.contact}
            onChange={update("contact")}
            maxLength={255}
            placeholder="Телефон, email или Telegram"
            required
          />
        </Field>
        <Field label="Чем занимается бизнес" id="partner-business" wide>
          <Textarea
            id="partner-business"
            value={form.business}
            onChange={update("business")}
            maxLength={700}
            rows={3}
            required
          />
        </Field>
        <Field
          label="Размер / характеристика аудитории"
          id="partner-audience"
          wide
        >
          <Textarea
            id="partner-audience"
            value={form.audience}
            onChange={update("audience")}
            maxLength={500}
            rows={3}
            required
          />
        </Field>
        <Field
          label="Как планируете рекомендовать VCENTRE.IT"
          id="partner-promotion"
          wide
        >
          <Textarea
            id="partner-promotion"
            value={form.promotion}
            onChange={update("promotion")}
            maxLength={1000}
            rows={4}
            required
          />
        </Field>
        <Field label="Комментарий" id="partner-comment" wide optional>
          <Textarea
            id="partner-comment"
            value={form.comment}
            onChange={update("comment")}
            maxLength={1000}
            rows={3}
          />
        </Field>
      </div>

      <Button
        type="submit"
        size="lg"
        variant="site"
        disabled={status === "sending"}
        className="w-full sm:w-auto"
      >
        {status === "sending" ? "Отправляем…" : "Отправить заявку"}
      </Button>
    </form>
  );
};

const Field = ({
  label,
  id,
  wide,
  optional,
  children,
}: {
  label: string;
  id: string;
  wide?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}) => (
  <div className={cn("space-y-2", wide && "sm:col-span-2")}>
    <Label htmlFor={id}>
      {label}
      {optional ? "" : " *"}
    </Label>
    {children}
  </div>
);

export default PartnerApplicationForm;
